import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import axios from 'axios';
import Timeline from './Timeline';
import { hasRoleAccess } from './utils/roles'; // Import role-checking helper
import 'primereact/resources/themes/saga-blue/theme.css'; // Import the theme
import 'primereact/resources/primereact.min.css'; // Core CSS
import 'primeicons/primeicons.css'; // Icons
import 'primeflex/primeflex.css'; // PrimeFlex CSS
import './GlobalStyles.css';

const ViewShippingItem = ({ userRole }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [locationEntries, setLocationEntries] = useState([]);
  const [itemLoading, setItemLoading] = useState(true);
  const [locationEntriesLoading, setLocationEntriesLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(100); // Number of items per page
  const [totalEntries, setTotalEntries] = useState(0); // Total items count
  const [sortField, setSortField] = useState('DatePlaced');
  const [sortOrder, setSortOrder] = useState(-1); // -1 for DESC, 1 for ASC
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [firstLocationPhoto, setFirstLocationPhoto] = useState(null);

  // Helper function to display a value or a styled "No Value" if blank.
  const displayValue = (value) => {
    return value === undefined || value === null || value === "" ? (
      <span className="empty-field">No Value</span>
    ) : (
      value
    );
  };

  // Fetch shipping item details
  useEffect(() => {
    const fetchItem = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/api/shipping-items/${id}`);
        setItem(response.data);
      } catch (error) {
        console.error('Error fetching item:', error);
      } finally {
        setItemLoading(false);
      }
    };

    // Define constants for retrying location entries fetch
    const MAX_RETRIES = 5;
    const DELAY_MS = 3000; // 3 seconds

    // Fetch location entries (with retry logic for 404 errors)
    const fetchLocationEntries = async (retryCount = 0) => {
      try {
        const response = await axios.get(`${apiBaseUrl}/api/shipping-item/${id}/location-entries`, {
          params: {
            page: page + 1,
            limit,
            sortField,
            sortOrder: sortOrder === 1 ? 'ASC' : 'DESC',
          },
        });
        const fetchedEntries = response.data || [];
        setLocationEntries(fetchedEntries);
        setTotalEntries(fetchedEntries.length);

        // If at least one location entry exists, attempt to load its photo document.
        if (fetchedEntries.length > 0) {
          const firstEntry = fetchedEntries[0];
          try {
            const docsResponse = await axios.get(
              `${apiBaseUrl}/api/location-entry/${firstEntry.EntryID}/documents`
            );
            if (docsResponse.data && docsResponse.data.length > 0) {
              // Look for a document with "Photo" in its EntryNotes.
              const firstDoc = docsResponse.data.find(
                (doc) => doc.EntryNotes && doc.EntryNotes.includes('Photo')
              );
              if (firstDoc) {
                const blobResponse = await axios.get(
                  `${apiBaseUrl}/api/download-item/${firstEntry.EntryID}`,
                  {
                    params: { fileName: firstDoc.FileName },
                    responseType: 'blob',
                  }
                );
                const reader = new FileReader();
                reader.onloadend = () => {
                  const base64String = reader.result.split(',')[1]; // remove the data URL prefix
                  setFirstLocationPhoto(base64String);
                };
                reader.readAsDataURL(blobResponse.data);
              } else {
                setFirstLocationPhoto(null);
              }
            } else {
              setFirstLocationPhoto(null);
            }
          } catch (docsError) {
            // If the documents endpoint returns 404, treat it as no documents (and don't retry).
            if (docsError.response && docsError.response.status === 404) {
              setFirstLocationPhoto(null);
            } else {
              console.error('Error fetching documents:', docsError);
            }
          }
        }
        setLocationEntriesLoading(false);
      } catch (error) {
        if (error.response && error.response.status === 404 && retryCount < MAX_RETRIES) {
          console.warn(
            `Location entries not found (retry ${retryCount + 1}/${MAX_RETRIES}). Retrying in ${DELAY_MS / 1000} seconds...`
          );
          setTimeout(() => {
            fetchLocationEntries(retryCount + 1);
          }, DELAY_MS);
        } else {
          console.error('Error fetching location entries:', error);
          alert('Failed to fetch location entries: ' + error.message);
          setLocationEntriesLoading(false);
        }
      }
    };

    fetchItem();
    fetchLocationEntries();
  }, [id, page, limit, sortField, sortOrder, apiBaseUrl]);

  const handleBack = () => {
    navigate('/shipping-items');
  };

  const onPageChange = (event) => {
    setPage(event.page);
    setLimit(event.rows);
  };

  const onSort = (event) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder);
  };

  const actionBodyTemplate = (rowData) => (
    <div>
      <Link to={`/view-location-entry/${rowData.EntryID}`}>
        <button className="intable-view-button">View</button>
      </Link>
      {hasRoleAccess(userRole, 'location-update') && (
        <Link to={`/edit-location-entry/${rowData.EntryID}`}>
          {/* Hide Edit button on mobile */}
          <button className="intable-edit-button hide-on-mobile">Edit</button>
        </Link>
      )}
    </div>
  );

  // Determine overall loading state
  const loading = itemLoading || locationEntriesLoading;
  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!item) {
    return <div>Item not found</div>;
  }

  // Format the received date, if available
  const receivedDateFormatted = item.Rcvd_Date
    ? new Date(item.Rcvd_Date).toISOString().split('T')[0]
    : null;

  return (
    <div className="view-item-container">
      <h1>View Shipping Item</h1>

      {/* Display the first location's photo if available */}
      {firstLocationPhoto && (
        <div className="first-location-photo-container">
          <img
            src={`data:image/jpeg;base64,${firstLocationPhoto}`}
            alt="First Location Photo"
            style={{ maxWidth: '300px', marginBottom: '1rem' }}
          />
        </div>
      )}

      {/* Updated details section with new Carrier and Shipper fields and styled empty values */}
      <div className="item-details">
        <p>
          <strong>Received Date:</strong> {displayValue(receivedDateFormatted)}
        </p>
        <p>
          <strong>Shipping ID:</strong> {displayValue(item.ShippingID)}
        </p>
        <p>
          <strong>Quantity in Lot:</strong> {displayValue(item.QtyInLot)}
        </p>
        <p>
          <strong>Project Number:</strong> {displayValue(item.ProjectNumber)}
        </p>
        <p>
          <strong>Project Manager:</strong> {displayValue(item.ProjectManager)}
        </p>
        <p>
          <strong>Client:</strong> {displayValue(item.Company)}
        </p>
        <p>
          <strong>Carrier:</strong> {displayValue(item.Carrier)}
        </p>
        <p>
          <strong>Shipper:</strong> {displayValue(item.Shipper)}
        </p>
        <p>
          <strong>Description:</strong> {displayValue(item.Description)}
        </p>
        <p>
          <strong>Receiver:</strong> {displayValue(item.Receiver)}
        </p>
      </div>

      {hasRoleAccess(userRole, 'shipping-update') && (
        <button
          className="edit-shipping-item-button"
          onClick={() => navigate(`/edit-shipping-item/${id}`)}
        >
          Edit Item
        </button>
      )}

      {/* Timeline for location entries */}
      <Timeline entries={locationEntries.slice().reverse()} receivedDate={receivedDateFormatted} />

      <div className="location-entries-header">
        <h2>All Location Entries</h2>
        {hasRoleAccess(userRole, 'location-create') && (
          <Link to={`/create-location-entry/${id}`} className="link-button">
            <button className="create-location-button">Create Location Entry</button>
          </Link>
        )}
      </div>

      <div className="table-container">
        <DataTable
          value={locationEntries}
          paginator
          rows={limit}
          totalRecords={totalEntries}
          loading={loading}
          lazy
          first={page * limit}
          onPage={onPageChange}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={onSort}
        >
          {/* Always visible on mobile and desktop */}
          <Column field="EntryID" header="Entry ID" sortable />
          <Column field="Location" header="Location" sortable />
          {/* Hide on mobile */}
          <Column
            field="DatePlaced"
            header="Date Placed"
            sortable
            headerClassName="hide-on-mobile"
            bodyClassName="hide-on-mobile"
          />
          <Column
            field="Notes"
            header="Notes"
            sortable
            headerClassName="hide-on-mobile"
            bodyClassName="hide-on-mobile"
          />
          <Column
            field="Qty"
            header="Quantity"
            sortable
            headerClassName="hide-on-mobile"
            bodyClassName="hide-on-mobile"
          />
          <Column body={actionBodyTemplate} header="Actions" />
        </DataTable>
      </div>
      <button onClick={handleBack} className="global-back-button">
        Back
      </button>
    </div>
  );
};

export default ViewShippingItem;
